// frontend/src/dashboard/TrialWizardPage.tsx
//
// The 7-day-trial onboarding wizard. Renders in two shapes that share the same
// internal component:
//
//   • <TrialWizardPage />   — full-screen, used at /dashboard/billing/trial-wizard
//   • <TrialWizardModal />  — Radix dialog variant, used by TrialBanner's CTA
//
// Steps:
//   1. Modules    — toggle module checkboxes (price-aware cards)
//   2. Employees  — "how many employees will you track?" number input, live estimate
//   3. Payment    — Stripe Checkout (setup mode) via POST /api/billing/payment-method,
//                   plus "I'll add it later". If the platform's Stripe keys aren't
//                   configured the backend 503s and we surface it as a toast.
//
// Pricing revamp (2026-07-25) + payroll band (2026-07-29): every tenant always
// pays a flat $9/mo "core" platform fee. Some modules are flat-priced
// (invoicing, projects, esign), some bill per employee (hr $2/clock-in
// employee, monitoring $7/monitored employee, payroll a flat base covering an
// included allowance plus a per-employee rate beyond it), and the rest
// (payments, email, expenses) are included free. hr and monitoring bill
// DISJOINTLY per employee (an employee is clock-in-only XOR monitored,
// never both) — payroll stacks on top of whichever applies. Because there's
// no seat-counts data yet at signup time, step 2's estimate runs the entered
// employee count through the SHARED marketing/billing math
// (pricingMath.computeMonthlyTotal) rather than re-implementing it here — an
// estimate only; actual invoices meter real usage.
//
// At each step's Next, we PATCH the current tenant's subscription so the
// backend has the latest selection — see billingApi.updateMySubscription.
// `seatCount` is deprecated platform-wide and is never sent.
// Backend endpoint: PATCH /api/billing/subscription (see api.ts).

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Check, ChevronLeft, ChevronRight, CreditCard, Users, LayoutGrid,
  Sparkles, X, Lock, Info,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { billingApi } from '../modules/platform-billing/api';
import type { ModulePrice, TenantSubscription } from '../modules/platform-billing/types';
import { DIALOG_CONTENT, SPRING_SOFT } from '../design-system/motion';
import {
  FALLBACK_PRICES as MARKETING_FALLBACK_PRICES,
  FALLBACK_SEAT_BASES,
  FALLBACK_INCLUDED_SEATS,
  computeMonthlyTotal,
} from '../marketing/pricingMath';

/* ────────────────────────────────────────────────────────────────────
 * Module catalogue
 * ────────────────────────────────────────────────────────────────────
 * The wizard surfaces these nine module keys. Display name and one-liner
 * description live here; live prices are merged in from the server-side
 * module_prices catalogue (so admin changes propagate without a code
 * change). Keys MUST match those in migrations/058_pricing_revamp.sql.
 */
type ModuleCard = {
  key:        string;
  name:       string;
  blurb:      string;
  /** Modules that cannot be turned off (foundational platform). */
  required?:  boolean;
};

const MODULE_CARDS: ModuleCard[] = [
  { key: 'invoicing',        name: 'Invoicing',        blurb: 'Issue invoices, get paid via Stripe links, chase the late ones.' },
  { key: 'hr',               name: 'HR',               blurb: 'People, attendance, leave, and the org chart.' },
  { key: 'payroll',          name: 'Payroll',          blurb: 'Runs, payslips, bank export. Depends on HR.' },
  { key: 'payments',         name: 'Payments',         blurb: 'Process card payments and reconcile incoming money.' },
  { key: 'projects',         name: 'Projects',         blurb: 'Tasks, time entries, and margin per client.' },
  { key: 'monitoring',       name: 'Monitoring',       blurb: 'Opt-in desktop activity for billable focus tracking.' },
  { key: 'email',            name: 'Email',            blurb: 'Send notifications, reminders, and customer messages.' },
  { key: 'expenses',         name: 'Expenses',         blurb: 'Capture receipts, approve claims, reimburse staff.' },
  { key: 'esign',            name: 'E-Sign',           blurb: 'Send documents for legally binding electronic signature.' },
  { key: 'platform-billing', name: 'Platform billing', blurb: 'Your subscription and invoices live here.', required: true },
];

/** Fallback prices used only when the server catalogue is still loading. Kept
 *  in sync with migrations/058_pricing_revamp.sql (+ the payroll-band
 *  migration) by re-using the marketing calculator's single source of truth
 *  (frontend/src/marketing/pricingMath.ts) rather than duplicating numbers. */
const FALLBACK_PRICES: Record<string, { base: number; seat: number; included: number }> = {
  invoicing:        { base: MARKETING_FALLBACK_PRICES.invoicing, seat: 0, included: 0 },
  hr:               { base: 0, seat: MARKETING_FALLBACK_PRICES.hr, included: FALLBACK_INCLUDED_SEATS.hr },
  payroll:          { base: FALLBACK_SEAT_BASES.payroll, seat: MARKETING_FALLBACK_PRICES.payroll, included: FALLBACK_INCLUDED_SEATS.payroll },
  payments:         { base: 0, seat: 0, included: 0 },
  projects:         { base: MARKETING_FALLBACK_PRICES.projects, seat: 0, included: 0 },
  monitoring:       { base: 0, seat: MARKETING_FALLBACK_PRICES.monitoring, included: FALLBACK_INCLUDED_SEATS.monitoring },
  email:            { base: 0, seat: 0, included: 0 },
  expenses:         { base: 0, seat: 0, included: 0 },
  esign:            { base: MARKETING_FALLBACK_PRICES.esign, seat: 0, included: 0 },
  'platform-billing': { base: 0, seat: 0, included: 0 },
};

/** Flat $9/mo platform fee every tenant always pays — not a toggleable
 *  module. Fallback used only while the server price catalogue is loading. */
const CORE_FALLBACK_PRICE = MARKETING_FALLBACK_PRICES.core;

/** module_key → the seat vocabulary noun used in per-employee pricing copy.
 *  Payroll bills active headcount, not who got actually paid — plain
 *  "employee" (2026-07-29). */
const SEAT_LABEL: Record<string, string> = {
  hr: 'clock-in employee',
  monitoring: 'monitored employee',
  payroll: 'employee',
};

/** Whole-dollar amounts render without a decimal; fractional ones keep two
 *  places (e.g. Payroll's $1.50/employee overage rate). */
function fmtAmt(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}

/* ────────────────────────────────────────────────────────────────────
 * Top-level entry points
 * ──────────────────────────────────────────────────────────────────── */

/**
 * Standalone-page version of the wizard, mounted at
 * `/dashboard/billing/trial-wizard`. Closing the wizard navigates back to the
 * billing page.
 */
export function TrialWizardPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen w-full bg-pearl-50 dark:bg-ink-900">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <WizardCore
          surface="page"
          onClose={() => navigate('/dashboard/billing')}
          onComplete={() => navigate('/dashboard/billing')}
        />
      </div>
    </div>
  );
}

/**
 * Modal version of the wizard for use inside the dashboard (e.g. from the
 * TrialBanner CTA). Uses Radix Dialog for focus trapping + a11y; appearance is
 * "full-screen sheet" so users have plenty of space for the module grid.
 */
export function TrialWizardModal({
  open, onOpenChange, subscription,
}: {
  open:           boolean;
  onOpenChange:   (open: boolean) => void;
  subscription?:  TenantSubscription | null;
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed inset-0 sm:inset-4 lg:inset-x-[50%] lg:translate-x-[-50%] lg:max-w-3xl',
            'z-50 overflow-y-auto bg-pearl-50 dark:bg-ink-900',
            'sm:rounded-3xl shadow-card-lg ring-1 ring-border/30 dark:ring-white/10',
            'duration-200',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        >
          <DialogPrimitive.Title className="sr-only">Set up your subscription</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Choose your modules, estimate your employee count, and add a payment method to convert your trial to a paid plan.
          </DialogPrimitive.Description>

          <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
            <WizardCore
              surface="modal"
              subscription={subscription ?? null}
              onClose={() => onOpenChange(false)}
              onComplete={() => onOpenChange(false)}
            />
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * Wizard core
 * ──────────────────────────────────────────────────────────────────── */

type Step = 1 | 2 | 3;

interface WizardCoreProps {
  surface:        'page' | 'modal';
  subscription?:  TenantSubscription | null;
  onClose:        () => void;
  onComplete:     () => void;
}

function WizardCore({ surface, subscription, onClose, onComplete }: WizardCoreProps) {
  const [step, setStep]               = React.useState<Step>(1);
  const [sub, setSub]                 = React.useState<TenantSubscription | null>(subscription ?? null);
  const [loadingSub, setLoadingSub]   = React.useState<boolean>(subscription === undefined);
  const [prices, setPrices]           = React.useState<ModulePrice[] | null>(null);
  const [saving, setSaving]           = React.useState<boolean>(false);

  // Local form state — seeded once the subscription loads.
  const [selected,  setSelected]  = React.useState<Set<string>>(new Set());
  /** Estimated employee count — drives the per-employee estimate on step 2.
   *  Defaults to 3. Never sent to the backend as `seatCount` (deprecated). */
  const [employees, setEmployees] = React.useState<number>(3);
  const [seeded,    setSeeded]    = React.useState<boolean>(false);

  // Load subscription if not supplied.
  React.useEffect(() => {
    if (subscription !== undefined) { setSub(subscription); setLoadingSub(false); return; }
    let cancelled = false;
    billingApi.mySubscription()
      .then((s) => { if (!cancelled) { setSub(s); setLoadingSub(false); } })
      .catch(() => { if (!cancelled) { setLoadingSub(false); } });
    return () => { cancelled = true; };
  }, [subscription]);

  // Load prices (best-effort — falls back to FALLBACK_PRICES if missing).
  React.useEffect(() => {
    let cancelled = false;
    billingApi.listMyModulePrices()
      .then((p) => { if (!cancelled) setPrices(p); })
      .catch(() => { /* silent — fallbacks kick in */ });
    return () => { cancelled = true; };
  }, []);

  // Seed form state from the loaded subscription exactly once.
  React.useEffect(() => {
    if (seeded || !sub) return;
    const keys = sub.enabled_module_keys && sub.enabled_module_keys.length > 0
      ? sub.enabled_module_keys
      : MODULE_CARDS.filter((m) => m.key !== 'platform-billing').map((m) => m.key);
    const next = new Set<string>(keys);
    // Always include any forced-required modules.
    for (const m of MODULE_CARDS) if (m.required) next.add(m.key);
    setSelected(next);
    // Employee count is an estimate only (never persisted as seat_count) —
    // keep the default of 3 regardless of what the subscription snapshot has.
    setSeeded(true);
  }, [sub, seeded]);

  /** Effective price for a module — server value if present, else fallback. */
  const priceFor = React.useCallback((key: string): { base: number; seat: number; included: number } => {
    if (prices) {
      const row = prices.find((p) => p.module_key === key);
      if (row) {
        return {
          base: Number(row.amount_per_month) || 0,
          seat: Number(row.seat_amount_per_month ?? 0) || 0,
          included: Number(row.included_seats ?? FALLBACK_PRICES[key]?.included ?? 0) || 0,
        };
      }
    }
    return FALLBACK_PRICES[key] ?? { base: 0, seat: 0, included: 0 };
  }, [prices]);

  /** The always-on $9/mo core platform fee — live from the server catalogue's
   *  `core` price row when available, else the fallback. */
  const corePrice = React.useMemo(() => {
    const row = prices?.find((p) => p.module_key === 'core');
    return row ? Number(row.amount_per_month) || 0 : CORE_FALLBACK_PRICE;
  }, [prices]);

  /** Currency for the running total — defaults to USD before sub loads. */
  const currency = sub?.billing_currency ?? 'USD';

  /**
   * Estimated monthly total: the flat $9 core fee, plus each selected
   * module's flat base, plus the entered employee count × a per-employee
   * rate. This is a pre-signup ESTIMATE — real invoices meter live employee
   * counts per module (see ManagePlanDialog / GET /api/billing/seat-counts
   * once subscribed).
   *
   * The arithmetic itself is NOT re-implemented here — it runs through
   * `computeMonthlyTotal` in marketing/pricingMath.ts, the same pure function
   * the pricing page's calculator uses, so the wizard's estimate cannot drift
   * from the public quote (the payroll band in particular used to be inlined
   * twice). The wizard has no per-person monitoring picker, so it passes
   * `monitored = employees` when Monitoring is selected — which is exactly the
   * disjoint hr-XOR-monitoring split the engine applies.
   */
  const total = React.useMemo(() => {
    const hrOn         = selected.has('hr');
    const monitoringOn = selected.has('monitoring');
    const payrollOn    = selected.has('payroll');
    const payrollPrice = priceFor('payroll');

    // Flat bases for any OTHER selected module the shared math doesn't model
    // (payments / email / expenses are free today, but an admin could price
    // one — this keeps the estimate honest if that ever happens).
    const SHARED = new Set(['platform-billing', 'invoicing', 'projects', 'esign', 'hr', 'monitoring', 'payroll']);
    let extraFlats = 0;
    selected.forEach((key) => { if (!SHARED.has(key)) extraFlats += priceFor(key).base; });

    const b = computeMonthlyTotal({
      corePrice,
      invoicingPrice: priceFor('invoicing').base,
      projectsPrice:  priceFor('projects').base,
      esignPrice:     priceFor('esign').base,
      hrSeatPrice:         priceFor('hr').seat,
      monitoringSeatPrice: priceFor('monitoring').seat,
      payrollBasePrice: payrollPrice.base,
      payrollSeatPrice: payrollPrice.seat,
      payrollIncluded:  payrollPrice.included,
      invoicingOn: selected.has('invoicing'),
      projectsOn:  selected.has('projects'),
      esignOn:     selected.has('esign'),
      hrOn, monitoringOn, payrollOn,
      employees,
      monitored: monitoringOn ? employees : 0,
    });

    // Display-only: the single per-employee rate the summary quotes (monitoring
    // supersedes hr for the same person, so at most one applies).
    const perEmployeeRate = monitoringOn
      ? priceFor('monitoring').seat
      : hrOn ? priceFor('hr').seat : 0;

    return {
      core: b.core,
      flats: b.flats + extraFlats,
      perEmployeeRate,
      payrollOn,
      payrollCost: b.payrollCost,
      total: b.total + extraFlats,
    };
  }, [selected, employees, priceFor, corePrice]);

  function toggle(key: string) {
    const card = MODULE_CARDS.find((m) => m.key === key);
    if (card?.required) return; // cannot toggle off platform-billing
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  /** Persist current selections. Best-effort — surfaces an error toast but
   *  does not block the wizard, so the user can keep moving on a flaky network. */
  async function persist(patch: { enabledModuleKeys?: string[] }): Promise<boolean> {
    setSaving(true);
    try {
      const next = await billingApi.updateMySubscription(patch);
      setSub(next);
      return true;
    } catch (err: any) {
      toast.error('Could not save your selection', { description: err?.message ?? 'Try again in a moment.' });
      return false;
    } finally {
      setSaving(false);
    }
  }

  function selectedAsArray(): string[] {
    const out: string[] = [];
    selected.forEach((k) => out.push(k));
    return out;
  }

  async function goNext() {
    if (step === 1) {
      // Every subscription is paid via the unconditional $9/mo core fee, so
      // any module selection (including none) keeps an active subscription —
      // no "pick at least one paid module" gate needed.
      await persist({ enabledModuleKeys: selectedAsArray() });
      setStep(2);
      return;
    }
    if (step === 2) {
      // Employee count is an estimate only — never sent to the backend
      // (seatCount is deprecated). Re-persisting modules here is harmless
      // and keeps this step consistent with step 1's save-on-advance pattern.
      await persist({ enabledModuleKeys: selectedAsArray() });
      setStep(3);
      return;
    }
    if (step === 3) {
      // Card save happens via the in-step Stripe redirect; "Finish" simply
      // closes the wizard (selections were already persisted at each step).
      onComplete();
    }
  }

  function goBack() {
    if (step === 1) { onClose(); return; }
    setStep((step - 1) as Step);
  }

  // While the subscription is loading we render a thin skeleton — keeps the
  // wizard from flashing empty content if it's opened mid-fetch.
  if (loadingSub && !sub) {
    return (
      <div className="py-20 text-center text-[14px] text-ink/55 dark:text-white/55">
        Loading your subscription…
      </div>
    );
  }

  return (
    <div>
      <WizardHeader step={step} surface={surface} onClose={onClose} />

      <AnimatePresence mode="wait">
        <motion.div
          key={`step-${step}`}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0, transition: SPRING_SOFT }}
          exit={{ opacity: 0, y: -6, transition: { duration: 0.14 } }}
        >
          {step === 1 && (
            <Step1Modules
              selected={selected}
              onToggle={toggle}
              priceFor={priceFor}
              currency={currency}
            />
          )}
          {step === 2 && (
            <Step2Employees
              employees={employees}
              onChange={setEmployees}
              total={total}
              currency={currency}
              selectedCount={selected.size}
              hrOn={selected.has('hr')}
              monitoringOn={selected.has('monitoring')}
              hrSeatRate={priceFor('hr').seat}
              monitoringSeatRate={priceFor('monitoring').seat}
              payrollIncluded={priceFor('payroll').included}
            />
          )}
          {step === 3 && (
            <Step3Payment
              total={total.total}
              currency={currency}
              onSkip={onComplete}
            />
          )}
        </motion.div>
      </AnimatePresence>

      <WizardFooter
        step={step}
        canAdvance={
          step === 2 ? employees >= 1 && employees <= 5000 : true
        }
        saving={saving}
        onBack={goBack}
        onNext={goNext}
      />
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * Header & step indicator
 * ──────────────────────────────────────────────────────────────────── */

function WizardHeader({ step, surface, onClose }: { step: Step; surface: 'page' | 'modal'; onClose: () => void }) {
  return (
    <div className="mb-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-coral/10 text-coral-700 dark:text-coral-300 text-[11px] font-bold uppercase tracking-[0.16em]">
            <Sparkles className="h-3 w-3" strokeWidth={2.5} />
            Trial onboarding
          </div>
          <h1 className="mt-3 font-light tracking-[-0.025em] text-display-page leading-tight text-ink dark:text-white">
            {step === 1 && 'Choose what your team needs'}
            {step === 2 && 'How many employees will you track?'}
            {step === 3 && 'Add a payment method'}
          </h1>
          <p className="mt-2 text-[14px] text-ink/60 dark:text-white/60 max-w-xl">
            {step === 1 && 'We turned everything on for your trial. Keep what you need, drop the rest — your bill is based only on the modules you pick.'}
            {step === 2 && 'This is just an estimate — your real bill is metered from live employee counts once you’re subscribed. You can change this any time from billing settings.'}
            {step === 3 && 'Securely save a card so your workspace keeps running after the trial ends. You can also skip and add it later.'}
          </p>
        </div>

        {surface === 'modal' && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 inline-flex items-center justify-center h-10 w-10 rounded-full bg-white/60 dark:bg-white/[0.06] hover:bg-white dark:hover:bg-white/10 ring-1 ring-ink/8 dark:ring-white/10 transition-colors"
          >
            <X className="h-4 w-4 text-ink/60 dark:text-white/70" strokeWidth={2.2} />
          </button>
        )}
      </div>

      <StepIndicator step={step} />
    </div>
  );
}

function StepIndicator({ step }: { step: Step }) {
  const items = [
    { n: 1 as Step, label: 'Modules',   Icon: LayoutGrid },
    { n: 2 as Step, label: 'Employees', Icon: Users },
    { n: 3 as Step, label: 'Payment',   Icon: CreditCard },
  ];
  return (
    <ol className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3" aria-label="Wizard progress">
      {items.map(({ n, label, Icon }) => {
        const state: 'done' | 'current' | 'todo' =
          n < step ? 'done' : n === step ? 'current' : 'todo';
        return (
          <li key={n} className="flex items-center gap-3">
            <div
              className={cn(
                'shrink-0 h-9 w-9 rounded-full inline-flex items-center justify-center text-[12px] font-bold transition-colors',
                state === 'done'    && 'bg-ink text-white dark:bg-white dark:text-ink',
                state === 'current' && 'bg-coral text-white shadow-cta',
                state === 'todo'    && 'bg-white/70 dark:bg-white/[0.06] text-ink/45 dark:text-white/45 ring-1 ring-ink/8 dark:ring-white/10',
              )}
            >
              {state === 'done' ? <Check className="h-4 w-4" strokeWidth={2.5} /> : <Icon className="h-4 w-4" strokeWidth={2.2} />}
            </div>
            <div className="min-w-0 leading-tight">
              <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-ink/55 dark:text-white/55">
                Step {n} / 3
              </div>
              <div className={cn(
                'text-[13px] font-semibold truncate',
                state === 'todo' ? 'text-ink/40 dark:text-white/40' : 'text-ink dark:text-white',
              )}>
                {label}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * Step 1 — Modules
 * ──────────────────────────────────────────────────────────────────── */

function Step1Modules({
  selected, onToggle, priceFor, currency,
}: {
  selected:   Set<string>;
  onToggle:   (key: string) => void;
  priceFor:   (key: string) => { base: number; seat: number; included: number };
  currency:   string;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
      {MODULE_CARDS.map((m) => {
        const price       = priceFor(m.key);
        const isOn        = selected.has(m.key);
        const isFree      = price.base === 0 && price.seat === 0;
        return (
          <button
            type="button"
            key={m.key}
            onClick={() => onToggle(m.key)}
            disabled={m.required}
            className={cn(
              'relative text-left rounded-3xl p-5 shadow-card ring-1 transition-all',
              'bg-white/72 dark:bg-white/[0.04] backdrop-blur-xl',
              isOn
                ? 'ring-coral/40 dark:ring-coral/50 outline outline-2 outline-coral/30'
                : 'ring-ink/[0.08] dark:ring-white/10 hover:ring-ink/15 dark:hover:ring-white/20',
              m.required && 'opacity-90 cursor-default',
            )}
            aria-pressed={isOn}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-[15px] font-semibold text-ink dark:text-white truncate">{m.name}</h3>
                  {m.required && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] text-ink/55 dark:text-white/55">
                      <Lock className="h-3 w-3" strokeWidth={2.5} />
                      Core
                    </span>
                  )}
                </div>
                <p className="mt-1 text-[12.5px] text-ink/60 dark:text-white/60 leading-snug">{m.blurb}</p>
              </div>

              <span
                className={cn(
                  'shrink-0 h-6 w-6 rounded-full inline-flex items-center justify-center transition-colors',
                  isOn
                    ? 'bg-coral text-white'
                    : 'bg-white dark:bg-white/10 ring-1 ring-ink/15 dark:ring-white/15',
                )}
                aria-hidden
              >
                {isOn && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
              </span>
            </div>

            <div className="mt-4 flex items-baseline gap-2 flex-wrap">
              {isFree ? (
                <span className="text-[13px] font-bold text-ink/55 dark:text-white/55 tracking-wide">Included</span>
              ) : (
                <>
                  {price.base > 0 && (
                    <>
                      <span className="text-[18px] font-bold tabular-nums text-ink dark:text-white">
                        {fmtAmt(price.base)} {currency}
                      </span>
                      <span className="text-[12px] text-ink/55 dark:text-white/55">/ mo flat</span>
                    </>
                  )}
                  {price.seat > 0 && (
                    <span className="ml-2 inline-flex items-baseline gap-1 text-[12.5px] font-semibold text-coral-700 dark:text-coral-300">
                      <span className="tabular-nums">+ {fmtAmt(price.seat)} {currency}</span>
                      <span className="text-ink/55 dark:text-white/55 font-normal">
                        / {SEAT_LABEL[m.key] ?? 'employee'}{price.included > 0 ? ` beyond ${price.included}` : ''}
                      </span>
                    </span>
                  )}
                </>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * Step 2 — Employees
 * ──────────────────────────────────────────────────────────────────── */

function Step2Employees({
  employees, onChange, total, currency, selectedCount,
  hrOn, monitoringOn, hrSeatRate, monitoringSeatRate, payrollIncluded,
}: {
  employees:            number;
  onChange:             (n: number) => void;
  total:                { core: number; flats: number; perEmployeeRate: number; payrollOn: boolean; payrollCost: number; total: number };
  currency:             string;
  selectedCount:        number;
  hrOn:                 boolean;
  monitoringOn:         boolean;
  hrSeatRate:           number;
  monitoringSeatRate:   number;
  payrollIncluded:      number;
}) {
  function clamp(n: number) { return Math.max(1, Math.min(5000, Math.round(n || 1))); }
  return (
    <div className="space-y-6">
      <section className="rounded-3xl bg-white/72 dark:bg-white/[0.04] backdrop-blur-xl shadow-card ring-1 ring-ink/[0.08] dark:ring-white/10 p-6 sm:p-8">
        <label htmlFor="trial-employees" className="block text-[11px] font-bold uppercase tracking-[0.16em] text-ink/55 dark:text-white/55">
          How many employees will you track?
        </label>

        <div className="mt-4 flex items-end gap-4 sm:gap-6 flex-wrap">
          <div className="flex items-baseline gap-2">
            <span className="font-light text-stat-hero leading-none tabular-nums text-ink dark:text-white">
              {employees}
            </span>
            <span className="text-[13px] text-ink/55 dark:text-white/55">
              {employees === 1 ? 'employee' : 'employees'}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onChange(clamp(employees - 1))}
              aria-label="Decrease employees"
              className="h-10 w-10 rounded-full bg-white dark:bg-white/10 shadow-pill ring-1 ring-ink/8 dark:ring-white/15 inline-flex items-center justify-center text-ink dark:text-white hover:bg-pearl-100 dark:hover:bg-white/15 transition-colors"
            >
              <span className="text-lg leading-none">−</span>
            </button>
            <input
              id="trial-employees"
              type="number"
              min={1}
              max={5000}
              value={employees}
              onChange={(e) => onChange(clamp(Number(e.target.value)))}
              className="w-20 h-10 text-center rounded-2xl bg-white dark:bg-white/[0.06] ring-1 ring-ink/8 dark:ring-white/15 text-[14px] font-semibold tabular-nums text-ink dark:text-white shadow-pill focus:outline-none focus:ring-2 focus:ring-coral/40"
            />
            <button
              type="button"
              onClick={() => onChange(clamp(employees + 1))}
              aria-label="Increase employees"
              className="h-10 w-10 rounded-full bg-white dark:bg-white/10 shadow-pill ring-1 ring-ink/8 dark:ring-white/15 inline-flex items-center justify-center text-ink dark:text-white hover:bg-pearl-100 dark:hover:bg-white/15 transition-colors"
            >
              <span className="text-lg leading-none">+</span>
            </button>
          </div>
        </div>

        <div className="mt-5 flex items-start gap-2 text-[12px] text-ink/55 dark:text-white/55">
          <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" strokeWidth={2.2} />
          <p>
            {monitoringOn ? (
              <>
                We estimate monitored employees at ${monitoringSeatRate.toFixed(0)}/employee
                (clock-in tracking is included — you're not charged twice). Your real bill
                is metered from live counts once you're subscribed.
              </>
            ) : hrOn ? (
              <>
                We estimate hr at ${hrSeatRate.toFixed(0)}/employee for everyone you track —
                add ${(monitoringSeatRate - hrSeatRate).toFixed(0)}/employee for any you monitor
                with screenshots (${monitoringSeatRate.toFixed(0)}/employee total for
                monitoring). Your real bill is metered from live counts once you're subscribed.
              </>
            ) : (
              <>Your real bill is metered from live employee counts once you're subscribed.</>
            )}
            {total.payrollOn && (
              <>
                {' '}Payroll is separate — its flat base fee includes your first{' '}
                {payrollIncluded} employees, then a per-employee rate for anyone beyond that.
              </>
            )}
          </p>
        </div>
      </section>

      {/* Live total card */}
      <section className="rounded-3xl bg-ink-900 text-white shadow-card-lg ring-1 ring-white/10 p-6 sm:p-7">
        <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-white/55">
          Estimated monthly bill
        </div>
        <div className="mt-2 flex items-baseline gap-2">
          <motion.span
            key={total.total.toFixed(2)}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0, transition: SPRING_SOFT }}
            className="font-bold text-stat-lg leading-none tabular-nums"
          >
            {total.total.toFixed(2)}
          </motion.span>
          <span className="text-[16px] text-white/65 font-medium">{currency}</span>
          <span className="text-[13px] text-white/55 ml-1">/ month</span>
        </div>
        <div className={cn('mt-4 grid gap-3 text-[12.5px]', total.payrollOn ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-3')}>
          <div className="rounded-2xl bg-white/[0.06] ring-1 ring-white/10 p-3">
            <div className="text-white/55">Core</div>
            <div className="mt-0.5 font-semibold tabular-nums">{total.core.toFixed(2)} {currency}</div>
          </div>
          <div className="rounded-2xl bg-white/[0.06] ring-1 ring-white/10 p-3">
            <div className="text-white/55">Module flats</div>
            <div className="mt-0.5 font-semibold tabular-nums">{total.flats.toFixed(2)} {currency}</div>
          </div>
          <div className="rounded-2xl bg-white/[0.06] ring-1 ring-white/10 p-3">
            <div className="text-white/55">{employees} × {total.perEmployeeRate.toFixed(2)} / employee</div>
            <div className="mt-0.5 font-semibold tabular-nums">{(employees * total.perEmployeeRate).toFixed(2)} {currency}</div>
          </div>
          {total.payrollOn && (
            <div className="rounded-2xl bg-white/[0.06] ring-1 ring-white/10 p-3">
              <div className="text-white/55">Payroll · includes {payrollIncluded}</div>
              <div className="mt-0.5 font-semibold tabular-nums">{total.payrollCost.toFixed(2)} {currency}</div>
            </div>
          )}
        </div>
        <div className="mt-4 text-[12px] text-white/55">
          {selectedCount} module{selectedCount === 1 ? '' : 's'} selected · billed monthly after your 7-day trial
        </div>
      </section>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * Step 3 — Payment (Stripe Checkout setup-mode redirect)
 * ──────────────────────────────────────────────────────────────────── */

function Step3Payment({
  total, currency, onSkip,
}: {
  total:     number;
  currency:  string;
  onSkip:    () => void;
}) {
  const [connecting, setConnecting] = React.useState(false);

  // Same self-serve card-save flow (and error-toast pattern) as
  // BillingPage's PaymentMethodCard: request a Stripe Checkout setup
  // session and hard-redirect to it. A 503 (platform Stripe keys not
  // configured) or any other failure lands in the toast.
  async function addCard() {
    setConnecting(true);
    try {
      const r = await billingApi.addPaymentMethod();
      window.location.href = r.url;
    } catch (err: any) {
      toast.error('Could not start card setup', { description: err.message });
      setConnecting(false);
    }
  }

  return (
    <div className="space-y-6">
      <section className="relative overflow-hidden rounded-3xl bg-white/72 dark:bg-white/[0.04] backdrop-blur-xl shadow-card ring-1 ring-ink/[0.08] dark:ring-white/10 p-6 sm:p-8">
        {/* placeholder card visual */}
        <div className="absolute inset-x-6 top-6 sm:inset-x-8 sm:top-8 h-40 sm:h-48 rounded-2xl bg-ink-900 text-white shadow-card-lg ring-1 ring-white/10 p-5 flex flex-col justify-between pointer-events-none">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-white/55">Worqly</span>
            <CreditCard className="h-5 w-5 text-white/70" strokeWidth={2} />
          </div>
          <div>
            <div className="font-mono tabular-nums text-[18px] tracking-[0.18em] text-white/85">•••• •••• •••• ••••</div>
            <div className="mt-3 flex items-center justify-between text-[11px] uppercase tracking-[0.14em] text-white/55">
              <span>Cardholder</span>
              <span>Expires</span>
            </div>
          </div>
        </div>

        {/* spacer matching the placeholder card height */}
        <div className="h-44 sm:h-52" aria-hidden />

        <div className="mt-4 flex items-start gap-2 px-1">
          <Info className="h-4 w-4 mt-0.5 text-ink/55 dark:text-white/55 shrink-0" strokeWidth={2.2} />
          <p className="text-[13px] text-ink/65 dark:text-white/65 leading-relaxed">
            <span className="font-semibold text-ink dark:text-white">Cards are saved securely with Stripe.</span>{' '}
            You'll be redirected to Stripe Checkout to save a card — Worqly
            never sees your card number. Once saved, your workspace moves from
            trial to paid automatically and invoices settle on their own.
          </p>
        </div>

        <div className="mt-5 flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={addCard}
            disabled={connecting}
            className={cn(
              'inline-flex flex-1 items-center justify-center gap-2 h-11 px-5 rounded-full bg-coral text-white text-[13.5px] font-semibold shadow-cta transition-colors',
              connecting ? 'opacity-70 cursor-wait' : 'hover:bg-coral-600',
            )}
          >
            <CreditCard className="h-4 w-4" strokeWidth={2.2} />
            {connecting ? 'Redirecting to Stripe…' : 'Add payment method'}
          </button>
          <button
            type="button"
            onClick={onSkip}
            className="inline-flex items-center justify-center h-11 px-5 rounded-full bg-white dark:bg-white/[0.06] text-ink dark:text-white text-[13.5px] font-semibold shadow-pill ring-1 ring-ink/8 dark:ring-white/15 hover:bg-pearl-100 dark:hover:bg-white/10 transition-colors"
          >
            I'll add it later
          </button>
        </div>
      </section>

      <section className="rounded-3xl bg-white/55 dark:bg-white/[0.03] backdrop-blur-xl ring-1 ring-white/30 dark:ring-white/10 p-5 sm:p-6">
        <div className="flex items-baseline justify-between gap-4">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-ink/55 dark:text-white/55">
              You'll be charged after the trial
            </div>
            <div className="mt-1 text-[13px] text-ink/65 dark:text-white/65">
              First invoice covers the month following your trial period.
            </div>
          </div>
          <div className="text-right">
            <div className="font-bold text-[24px] tabular-nums text-ink dark:text-white">
              {total.toFixed(2)} <span className="text-[14px] font-medium text-ink/55 dark:text-white/55">{currency}</span>
            </div>
            <div className="text-[12px] text-ink/55 dark:text-white/55">/ month</div>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * Footer (back + next)
 * ──────────────────────────────────────────────────────────────────── */

function WizardFooter({
  step, canAdvance, saving, onBack, onNext,
}: {
  step:       Step;
  canAdvance: boolean;
  saving:     boolean;
  onBack:     () => void;
  onNext:     () => void;
}) {
  return (
    <div className="mt-8 flex items-center justify-between gap-3 pt-6 border-t border-ink/8 dark:border-white/10">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 h-11 px-5 rounded-full bg-white dark:bg-white/[0.06] text-ink dark:text-white text-[13.5px] font-semibold shadow-pill ring-1 ring-ink/8 dark:ring-white/15 hover:bg-pearl-100 dark:hover:bg-white/10 transition-colors"
      >
        <ChevronLeft className="h-4 w-4" strokeWidth={2.2} />
        {step === 1 ? 'Cancel' : 'Back'}
      </button>

      <div className="flex items-center gap-3">
        {saving && (
          <span className="text-[12px] text-ink/55 dark:text-white/55">Saving…</span>
        )}
        <button
          type="button"
          onClick={onNext}
          disabled={!canAdvance || saving}
          className={cn(
            'inline-flex items-center gap-1.5 h-11 px-6 rounded-full text-[13.5px] font-semibold transition-all',
            canAdvance
              ? 'bg-coral text-white shadow-cta hover:bg-coral-600'
              : 'bg-ink/10 dark:bg-white/10 text-ink/40 dark:text-white/40 cursor-not-allowed',
            saving && 'opacity-70 cursor-wait',
          )}
        >
          {step === 3 ? 'Finish' : 'Continue'}
          <ChevronRight className="h-4 w-4" strokeWidth={2.2} />
        </button>
      </div>
    </div>
  );
}

// Unused-import shim for variants kept on hand for future use.
void DIALOG_CONTENT;

export default TrialWizardPage;
